// src/utils/polygonInset.js

import offsetPolygon from 'offset-polygon';

const EPS = 1e-9;
const CLEAN_EPS = 1e-6;

function almostEqual(a, b, eps = EPS) {
  return Math.abs(a - b) <= eps;
}

function distance2D(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  return Math.hypot(dx, dz);
}

function polygonSignedArea(poly) {
  let area = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    area += p.x * q.z - q.x * p.z;
  }
  return area * 0.5;
}

function ensureClosedLoopRemoved(poly) {
  if (!poly || poly.length < 3) return poly;
  const first = poly[0];
  const last = poly[poly.length - 1];
  if (almostEqual(first.x, last.x) && almostEqual(first.z, last.z)) {
    return poly.slice(0, -1);
  }
  return [...poly];
}

function ensureCCW(poly) {
  const clean = ensureClosedLoopRemoved(poly);
  if (polygonSignedArea(clean) < 0) {
    return [...clean].reverse();
  }
  return clean;
}

function removeDuplicateSequentialPoints(poly, eps = CLEAN_EPS) {
  if (!poly || poly.length === 0) return poly;

  const out = [poly[0]];
  for (let i = 1; i < poly.length; i++) {
    if (distance2D(out[out.length - 1], poly[i]) > eps) {
      out.push(poly[i]);
    }
  }

  if (out.length > 1 && distance2D(out[0], out[out.length - 1]) <= eps) {
    out.pop();
  }

  return out;
}

function toXY(polyXZ) {
  return polyXZ.map((p) => ({ x: p.x, y: p.z }));
}

function toXZ(polyXY) {
  return polyXY.map((p) => ({ x: p.x, z: p.y }));
}

export function insetPolygonXZ(polygonXZ, insetDistance) {
  if (!polygonXZ || polygonXZ.length < 3) return null;
  if (!Number.isFinite(insetDistance) || insetDistance <= 0) return null;

  try {
    let poly = ensureCCW(polygonXZ);
    poly = removeDuplicateSequentialPoints(poly);

    if (!poly || poly.length < 3) return null;

    const xy = toXY(poly);

    // offset-polygon uses negative for inward padding
    const insetXY = offsetPolygon(xy, -insetDistance, 0);

    if (!insetXY || insetXY.length < 3) return null;

    let insetXZ = toXZ(insetXY);
    insetXZ = removeDuplicateSequentialPoints(insetXZ);

    if (!insetXZ || insetXZ.length < 3) return null;

    const area = Math.abs(polygonSignedArea(insetXZ));
    if (!Number.isFinite(area) || area < 1e-4) return null;

    return insetXZ;
  } catch (err) {
    console.error('insetPolygonXZ failed:', err);
    return null;
  }
}


export function getPolygonCentroidXZ(poly) {
  if (!poly || poly.length < 3) return null;

  let a = 0;
  let cx = 0;
  let cz = 0;

  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    const cross = p.x * q.z - q.x * p.z;
    a += cross;
    cx += (p.x + q.x) * cross;
    cz += (p.z + q.z) * cross;
  }

  a *= 0.5;
  if (Math.abs(a) < EPS) return null;

  return {
    x: cx / (6 * a),
    z: cz / (6 * a),
  };
}

export function isInsetPossible(polygonXZ, insetDistance) {
  return !!insetPolygonXZ(polygonXZ, insetDistance);
}

// Inset with a different offset per edge (road edge vs other edges).
// edgeOffsets[i] applies to the edge from polygonXZ[i] to polygonXZ[i+1],
// in the SAME ordering as the input polygon (order is preserved).
export function insetPolygonMixedXZ(polygonXZ, edgeOffsets) {
  if (!polygonXZ || polygonXZ.length < 3) return null;

  const poly = removeDuplicateSequentialPoints(ensureClosedLoopRemoved(polygonXZ));
  if (!poly || poly.length < 3) return null;

  const n = poly.length;

  // winding (do NOT reorder — edge indices must match the caller's polygon)
  const ccw = polygonSignedArea(poly) > 0;

  // each edge shifted inward by its own offset
  const lines = [];
  for (let i = 0; i < n; i++) {
    const a = poly[i], b = poly[(i + 1) % n];
    const dx = b.x - a.x, dz = b.z - a.z;
    const len = Math.hypot(dx, dz) || 1;
    const ux = dx / len, uz = dz / len;
    const nx = ccw ? -uz : uz;      // inward normal
    const nz = ccw ? ux : -ux;
    const off = Number.isFinite(edgeOffsets?.[i]) ? edgeOffsets[i] : 0;
    lines.push({ px: a.x + nx * off, pz: a.z + nz * off, dx: ux, dz: uz });
  }

  // new vertex i = intersection of offset lines (i-1) and (i)
  const out = [];
  for (let i = 0; i < n; i++) {
    const L1 = lines[(i - 1 + n) % n], L2 = lines[i];
    const denom = L1.dx * L2.dz - L1.dz * L2.dx;
    if (Math.abs(denom) < 1e-9) {
      out.push({ x: L2.px, z: L2.pz });
      continue;
    }
    const t = ((L2.px - L1.px) * L2.dz - (L2.pz - L1.pz) * L2.dx) / denom;
    out.push({ x: L1.px + L1.dx * t, z: L1.pz + L1.dz * t });
  }

  const cleaned = removeDuplicateSequentialPoints(out);
  if (!cleaned || cleaned.length < 3) return null;
  const area = Math.abs(polygonSignedArea(cleaned));
  if (!Number.isFinite(area) || area < 1e-4) return null;
  return cleaned;
}
